import React, { useState } from "react";
import useSWR from "swr";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { LogoMark } from "@/components/app/Logo";
import { listProjects, createProject, listCatalogs } from "@/lib/projectApi";
import CoverImage from "@/components/app/CoverImage";
import { fmtMoney } from "@/lib/format";
import { Plus, LogOut, Loader2, ArrowUpRight, MapPin, Users as UsersIcon, Settings2, Home, Building, KeyRound, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import ChangePasswordDialog from "@/components/app/ChangePasswordDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const PHASE_TONE = {
  "Concept": "bg-slate-500/10 text-slate-700 border-slate-500/40",
  "Design Development": "bg-blue-500/10 text-blue-700 border-blue-500/40",
  "Tender": "bg-purple-500/10 text-purple-700 border-purple-500/40",
  "Execution": "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
  "Snagging": "bg-amber-500/10 text-amber-700 border-amber-500/40",
  "Handover": "bg-teal-500/10 text-teal-700 border-teal-500/40",
};

export default function Projects() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: projects, mutate } = useSWR("/projects", listProjects);
  const { data: catalogs } = useSWR("/catalogs", listCatalogs);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", client_name: "", client_email: "", client_phone: "",
    project_type: "Residential", phase: "Concept",
    location: "Dubai, UAE", address: "", contract_value: 0, notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Project name required"); return; }
    setSaving(true);
    try {
      const p = await createProject({ ...form, contract_value: Number(form.contract_value) || 0 });
      toast.success(`Created "${p.name}"`);
      setOpen(false);
      mutate();
      navigate(`/projects/${p.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const projectTypes = catalogs?.project_types || [];
  const phases = catalogs?.phases || [];

  return (
    <div className="min-h-screen">
      <header className="sticky top-3 z-40 mx-auto max-w-7xl px-3 sm:px-4 lg:px-6">
        <div className="glass rounded-full px-4 sm:px-5 py-2.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <LogoMark size={34} />
            <div className="leading-tight">
              <div className="font-heading text-[10px] font-bold tracking-[0.28em] text-foreground/90">NAT HABITAT</div>
              <div className="font-heading text-sm font-semibold -mt-0.5">Back Office</div>
            </div>
            <div className="hidden sm:block h-6 w-px bg-border/60 mx-1" />
            <div className="hidden sm:block text-[10px] text-muted-foreground font-mono uppercase tracking-[0.2em]">
              {user?.email} · {user?.role}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {user?.role === "superadmin" && (
              <>
                <Link to="/admin/catalogs">
                  <Button variant="ghost" size="sm" className="h-8 rounded-full text-xs" data-testid="btn-admin-catalogs"><Settings2 className="h-3.5 w-3.5 mr-1" /> Admin</Button>
                </Link>
                <Link to="/users">
                  <Button variant="ghost" size="sm" className="h-8 rounded-full text-xs" data-testid="btn-manage-users"><UsersIcon className="h-3.5 w-3.5 mr-1" /> Team</Button>
                </Link>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 rounded-full text-xs" data-testid="btn-account-menu">
                  {user?.email?.split("@")[0] || "Account"}
                  <ChevronDown className="h-3.5 w-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                  {user?.email}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="menu-change-password">
                  <KeyRound className="h-4 w-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-signout" className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="font-heading text-2xl font-bold">Projects</h2>
            <p className="text-sm text-muted-foreground">Every villa, apartment or commercial fit-out you're running — with BOQs, accounting, drawings and permits in one place.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-project" className="shadow-glossy">
                <Plus className="h-4 w-4 mr-1" /> New project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>New project</DialogTitle>
                <DialogDescription>Set up a new villa, apartment or commercial fit-out. You can add vendors, BOQs, drawings and permits afterwards.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                <div>
                  <Label>Project name</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Villa Al Barari · Design & Fitout" autoFocus data-testid="input-project-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.project_type} onValueChange={v => setForm(f => ({...f, project_type: v}))}>
                      <SelectTrigger data-testid="select-project-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {projectTypes.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Phase</Label>
                    <Select value={form.phase} onValueChange={v => setForm(f => ({...f, phase: v}))}>
                      <SelectTrigger data-testid="select-project-phase"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {phases.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Client name</Label>
                    <Input value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))} placeholder="e.g. Shishir Shah" />
                  </div>
                  <div>
                    <Label>Client phone</Label>
                    <Input value={form.client_phone} onChange={e => setForm(f => ({...f, client_phone: e.target.value}))} placeholder="+971…" />
                  </div>
                </div>
                <div>
                  <Label>Client email</Label>
                  <Input value={form.client_email} onChange={e => setForm(f => ({...f, client_email: e.target.value}))} placeholder="client@example.com" />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} placeholder="Hattan 3, Villa 8A, The Lakes" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Location</Label>
                    <Input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} />
                  </div>
                  <div>
                    <Label>Contract value (AED)</Label>
                    <Input type="number" value={form.contract_value} onChange={e => setForm(f => ({...f, contract_value: e.target.value}))} placeholder="0" />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Anything worth remembering" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={saving} data-testid="btn-create-project">{saving ? "Creating…" : "Create project"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {!projects ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : projects.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-16 text-center bg-card/40 backdrop-blur">
            <Building className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No projects yet. Create your first one to get started.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {(() => {
              // Build ordered phase list from catalog so the admin can control the order.
              const phaseOrder = (catalogs?.phases || []).map(p => p.value);
              // Group projects by phase; unknown phases go to the end.
              const groups = new Map();
              phaseOrder.forEach(ph => groups.set(ph, []));
              for (const p of projects) {
                if (!groups.has(p.phase)) groups.set(p.phase, []);
                groups.get(p.phase).push(p);
              }
              // Preferred visual order: Execution first (day-to-day priority), then earlier phases,
              // then Snagging, then Handover last. Fall back to catalog order otherwise.
              const preferred = [
                "Execution",
                "Tender",
                "Design Development",
                "Concept",
                "Snagging",
                "Handover",
              ];
              const orderedKeys = [
                ...preferred.filter(k => groups.has(k)),
                ...[...groups.keys()].filter(k => !preferred.includes(k)),
              ];
              return orderedKeys
                .filter(k => (groups.get(k) || []).length > 0)
                .map(phase => {
                  const list = groups.get(phase);
                  const tone = PHASE_TONE[phase] || "bg-slate-500/10 text-slate-700 border-slate-500/40";
                  const isHandover = phase === "Handover";
                  return (
                    <section key={phase} data-testid={`phase-section-${phase.replace(/\s+/g, "-").toLowerCase()}`}>
                      <div className="flex items-baseline gap-3 mb-3">
                        <Badge variant="outline" className={`text-[11px] ${tone}`}>{phase}</Badge>
                        <h3 className="font-heading text-sm font-bold uppercase tracking-[0.22em] text-foreground">
                          {isHandover ? "Handed over" : phase}
                        </h3>
                        <span className="text-[11px] font-mono text-muted-foreground">· {list.length} project{list.length !== 1 ? "s" : ""}</span>
                        <div className="flex-1 h-px bg-border/70" />
                      </div>
                      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 ${isHandover ? "opacity-80" : ""}`}>
                        {list.map(p => {
                          const phaseCls = PHASE_TONE[p.phase] || "bg-slate-500/10 text-slate-700 border-slate-500/40";
                          return (
                            <button
                              key={p.id}
                              onClick={() => navigate(`/projects/${p.id}`)}
                              className="text-left rounded-2xl border border-border bg-card/70 backdrop-blur-xl hover:border-primary/50 hover:shadow-glossy transition-all group card-glossy overflow-hidden"
                              data-testid={`project-card-${p.id}`}
                            >
                              <div className="aspect-[16/9] bg-gradient-to-br from-primary/20 to-accent/20 relative overflow-hidden">
                                <CoverImage
                                  projectId={p.id}
                                  coverId={p.cover_id}
                                  alt={p.name}
                                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                                  fallback={
                                    <div className="w-full h-full grid place-items-center">
                                      {p.project_type === "Residential" ? <Home className="h-12 w-12 text-primary/40" /> : <Building className="h-12 w-12 text-primary/40" />}
                                    </div>
                                  }
                                />
                              </div>
                              <div className="p-5">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <Badge variant="outline" className="text-[10px]">{p.project_type}</Badge>
                                  <Badge variant="outline" className={`text-[10px] ${phaseCls}`}>{p.phase}</Badge>
                                  {p.permits_pending > 0 && (
                                    <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-700 border-amber-500/40">
                                      {p.permits_pending} permit{p.permits_pending > 1 ? "s" : ""} pending
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-start justify-between gap-2">
                                  <h3 className="font-heading text-lg font-bold group-hover:text-primary transition line-clamp-2">{p.name}</h3>
                                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition shrink-0" />
                                </div>
                                {p.client_name && <div className="text-xs text-muted-foreground mt-1">Client: <b>{p.client_name}</b></div>}
                                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                                  <MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{p.address || p.location}</span>
                                </div>
                                <div className="mt-4 pt-4 border-t border-border/60 grid grid-cols-3 gap-2 text-xs">
                                  <div>
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Awarded</div>
                                    <div className="num font-bold text-sm">{fmtMoney(p.awarded_total, false)}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Paid</div>
                                    <div className="num font-bold text-sm text-emerald-700">{fmtMoney(p.paid_total, false)}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Outstanding</div>
                                    <div className="num font-bold text-sm text-amber-700">{fmtMoney(p.outstanding, false)}</div>
                                  </div>
                                </div>
                                <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                                  {p.vendor_count} vendor{p.vendor_count !== 1 ? "s" : ""} · {p.boq_count} BOQ{p.boq_count !== 1 ? "s" : ""}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  );
                });
            })()}
          </div>
        )}
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
